import {
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { CONFIG_SERVICE } from '@auth';
import { Observable } from 'rxjs';

/**
 * Tenant Interceptor
 *
 * Automatically appends Tenant IDs as query parameters to outgoing HTTP requests if configured and required.
 */
export const tenantInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn,
): Observable<HttpEvent<any>> => {
  const configService = inject(CONFIG_SERVICE, { optional: true });
  const router = inject(Router);

  // Ensure the configuration service is properly configured.
  if (!configService?.getTenantConfig()) {
    console.error(
      'Missing getTenantConfig() on config service, cannot load Tenant Interceptor',
    );
    return next(req);
  }

  const config = configService.getTenantConfig();
  const debugMode: boolean = config.debugMode || false;
  const securedEndpoints: string[] = config.securedEndpoints || [];
  const tenantIds: string[] | null = config.tenantIds;
  const tenantIdPropertyName: string = config.tenantIdPropertyName;
  const redirectUrl: string = config.redirectUrl || '/no-redirect-tenant';

  // Check if the request URL matches any secured endpoint
  const isSecuredEndpoint = securedEndpoints.some((endpoint) =>
    req.url.startsWith(endpoint),
  );

  if (!isSecuredEndpoint) {
    // If not a secured endpoint, pass the request unmodified
    logDebug(debugMode, `Skipping Tenant Interceptor for URL: ${req.url}`);
    return next(req);
  }

  if (!tenantIds || tenantIds.length === 0) {
    console.warn('Tenant Interceptor Warning: Tenant IDs are not available');
    logDebug(debugMode, `Redirecting to URL: ${redirectUrl}`);
    router.navigateByUrl(redirectUrl);
    return next(req); // Proceed without adding the tenant query parameters
  }

  // Append the Tenant IDs as query parameters
  const modifiedUrl = addQueryParams(req.url, tenantIdPropertyName, tenantIds);

  const clonedRequest = req.clone({ url: modifiedUrl });
  logDebug(debugMode, `Tenant IDs added to URL: ${modifiedUrl}`);
  return next(clonedRequest);
};

/**
 * Appends multiple query parameters to a URL.
 * @param url - The original URL.
 * @param key - The query parameter key.
 * @param values - An array of query parameter values.
 * @returns The modified URL with the query parameters appended.
 */
function addQueryParams(url: string, key: string, values: string[]): string {
  const urlObj = new URL(url, location.origin);
  values.forEach((value) => urlObj.searchParams.append(key, value));
  return urlObj.toString();
}

/**
 * Logs debugging information if logging is enabled.
 * @param enableLogging - Flag to toggle logging.
 * @param message - Message to log.
 */
function logDebug(enableLogging: boolean, message: string): void {
  if (enableLogging) {
    console.debug(`[TenantInterceptor] ${message}`);
  }
}
